/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/useAssistantContext.ts
 * PURPOSE: Debounced loader for POST /assistant/context/resolve (Brain Context API v1).
 * DEPENDENCIES: react, @shared/lib/frontend/api/assistantContextApi
 * PUBLIC_API: useAssistantContext
 * SIDE_EFFECTS: HTTP POST on thread/message changes
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 120 / 150 lines
 * RECENT: 2026-06-08 [2.2.1357] — Optional **`queryTypeRef`** for immediate routed topic slug on stream complete.
 * RECENT_PRIOR: 2026-05-26 [2.2.1251] — refresh on BRAZN_BRAIN_ACTIVITY_CHANGED (Postmark poll, email sent).
 * RECENT_PRIOR: 2026-05-26 [2.2.1234] — full assistant refresh when catch-up detects new active memories.
 * RECENT_PRIOR: 2026-05-26 [2.2.1233] — notifyBrainDashboardRefresh on pipeline catch-up for Home sync.
 * RECENT_PRIOR: 2026-05-26 [2.2.1231] — dismissLearningCatchUpHint; mobile drawer peek wiring via AssistantPage.
 * RECENT_PRIOR: 2026-05-26 [2.2.1229] — learningCatchUpHint after post-chat pipeline catch-up refresh.
 * RECENT_PRIOR: 2026-05-26 [2.2.1228] — stale-while-revalidate refresh; keep rail visible during catch-up.
 * RECENT_PRIOR: 2026-05-26 [2.2.1227] — refreshWithCatchup for promotion_pipeline after chat fan-out lag.
 * RECENT_PRIOR: 2026-05-26 [2.2.1225] — refresh on tab return after 5s hidden (stale brain context).
 */

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { API_CONFIG, getAuthToken } from '@shared/lib/frontend/api/config';
import {
  resolveAssistantContext,
  type AssistantContextResponse,
  type AssistantShellContext,
} from '@shared/lib/frontend/api/assistantContextApi';
import {
  BRAZN_BRAIN_PREFERENCES_CHANGED,
  notifyBrainDashboardRefresh,
  notifyBrainPreferencesChanged,
  type BrainPreferencesChangedDetail,
} from '@shared/lib/frontend/utils/brainPreferenceEvents';
import {
  BRAZN_BRAIN_ACTIVITY_CHANGED,
  type BrainActivityChangedDetail,
} from '@shared/lib/frontend/utils/brainActivityEvents';
import {
  formatLearningPipelineCatchUpHint,
  snapshotLearningPipeline,
  countNewActiveMemories,
  type LearningPipelineSnapshot,
} from '../../components/assistant/assistantContextUtils';

export type UseAssistantContextOptions = {
  threadId?: string | null;
  userMessage?: string;
  queryType?: string;
  /** Synchronous topic slug override (stream routing before React state commits) [2.2.1357]. */
  queryTypeRef?: MutableRefObject<string | undefined>;
  enabled?: boolean;
  debounceMs?: number;
  /** CRM route focus for context/resolve when dock is open on entity pages [2.2.1255]. */
  focusEntityHint?: { kind?: string; entity_id?: string } | null;
  /** Live shell page + module snapshot for dock grounding [2.2.1345]. */
  shellContext?: AssistantShellContext | null;
  /** Refetch when user returns to the tab after being away (default true). */
  refreshOnReturn?: boolean;
  /** Minimum hidden duration before a visibility refetch (ms). */
  refreshOnReturnAfterMs?: number;
  /**
   * After each explicit refresh(), schedule additional refetches (ms) so promotion_pipeline
   * catches up after fan-out / memory scan lag [2.2.1227].
   */
  followUpRefreshDelaysMs?: number[];
};

function isDealDetailShellPath(pagePath?: string | null): boolean {
  const path = (pagePath ?? '').split('?')[0].trim();
  return /^\/crm\/deals\/[0-9a-f-]{36}$/i.test(path);
}

export type AssistantContextState = {
  context: AssistantContextResponse | null;
  /** True only on first load when no context is shown yet. */
  loading: boolean;
  /** True during background refetch while existing context stays visible. */
  refreshing: boolean;
  error: string | null;
  refresh: () => void;
  /** Immediate refresh plus follow-up delays for self-learning pipeline catch-up. */
  refreshWithCatchup: () => void;
  /** Brief user-facing note when catch-up refresh detects pipeline progress. */
  learningCatchUpHint: string | null;
  dismissLearningCatchUpHint: () => void;
};

export function useAssistantContext(options: UseAssistantContextOptions = {}): AssistantContextState {
  const {
    threadId,
    userMessage = '',
    queryType = 'general_chat',
    queryTypeRef,
    enabled = true,
    debounceMs = 650,
    focusEntityHint = null,
    shellContext = null,
    refreshOnReturn = true,
    refreshOnReturnAfterMs = 5000,
    followUpRefreshDelaysMs = [],
  } = options;

  const [context, setContext] = useState<AssistantContextResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [learningCatchUpHint, setLearningCatchUpHint] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const followUpTimersRef = useRef<number[]>([]);
  const hasLoadedRef = useRef(false);
  const contextRef = useRef<AssistantContextResponse | null>(null);
  const catchUpActiveRef = useRef(false);
  const catchUpBaselineRef = useRef<LearningPipelineSnapshot | null>(null);
  const catchUpEndTimerRef = useRef<number>();
  const catchUpHintTimerRef = useRef<number>();
  const dealRefreshPathRef = useRef<string | null>(null);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    hasLoadedRef.current = false;
    setContext(null);
    setError(null);
  }, [threadId]);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  const dismissLearningCatchUpHint = useCallback(() => {
    window.clearTimeout(catchUpHintTimerRef.current);
    setLearningCatchUpHint(null);
  }, []);

  const refreshWithCatchup = useCallback(() => {
    followUpTimersRef.current.forEach((id) => window.clearTimeout(id));
    followUpTimersRef.current = [];
    window.clearTimeout(catchUpEndTimerRef.current);
    catchUpBaselineRef.current = snapshotLearningPipeline(contextRef.current);
    catchUpActiveRef.current = true;
    refresh();
    const maxDelay = followUpRefreshDelaysMs.reduce((max, ms) => Math.max(max, ms), 0);
    catchUpEndTimerRef.current = window.setTimeout(() => {
      catchUpActiveRef.current = false;
    }, maxDelay + 2000);
    for (const delayMs of followUpRefreshDelaysMs) {
      if (delayMs <= 0) continue;
      const id = window.setTimeout(() => refresh(), delayMs);
      followUpTimersRef.current.push(id);
    }
  }, [followUpRefreshDelaysMs, refresh]);

  useEffect(
    () => () => {
      followUpTimersRef.current.forEach((id) => window.clearTimeout(id));
      followUpTimersRef.current = [];
      window.clearTimeout(catchUpEndTimerRef.current);
      window.clearTimeout(catchUpHintTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPrefsChanged = (event: Event) => {
      const scope = (event as CustomEvent<BrainPreferencesChangedDetail>).detail?.scope;
      if (scope === 'dashboard') return;
      if (followUpRefreshDelaysMs.length > 0) refreshWithCatchup();
      else refresh();
    };
    window.addEventListener(BRAZN_BRAIN_PREFERENCES_CHANGED, onPrefsChanged);
    return () => window.removeEventListener(BRAZN_BRAIN_PREFERENCES_CHANGED, onPrefsChanged);
  }, [followUpRefreshDelaysMs, refresh, refreshWithCatchup]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onActivityChanged = (event: Event) => {
      const detail = (event as CustomEvent<BrainActivityChangedDetail>).detail;
      const scope = detail?.scope;
      if (detail?.source === 'quota_sync') {
        refresh();
        return;
      }
      if (scope === 'dashboard' || scope === 'crm') return;
      refresh();
    };
    window.addEventListener(BRAZN_BRAIN_ACTIVITY_CHANGED, onActivityChanged);
    return () => window.removeEventListener(BRAZN_BRAIN_ACTIVITY_CHANGED, onActivityChanged);
  }, [refresh]);

  useEffect(() => {
    if (!enabled || !refreshOnReturn || typeof document === 'undefined') return;

    let hiddenAt: number | null = null;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible' || hiddenAt === null) return;
      if (Date.now() - hiddenAt >= refreshOnReturnAfterMs) refresh();
      hiddenAt = null;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, refresh, refreshOnReturn, refreshOnReturnAfterMs]);

  // Deal detail pages: force world-model rebuild when cache is sparse (coaching + beliefs).
  useEffect(() => {
    if (!enabled || !isDealDetailShellPath(shellContext?.page_path)) return;

    const path = (shellContext?.page_path ?? '').split('?')[0].trim();
    if (dealRefreshPathRef.current === path) return;
    dealRefreshPathRef.current = path;

    const base = (API_CONFIG.baseURL || '').replace(/\/$/, '');
    void (async () => {
      try {
        const token = await getAuthToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        await fetch(`${base}/api/v1/assistant/world-model/refresh`, {
          method: 'POST',
          headers,
          credentials: 'include',
        });
      } catch {
        /* silent — never block dock */
      }
    })();

    if (followUpRefreshDelaysMs.length > 0) refreshWithCatchup();
    else {
      const id = window.setTimeout(() => refresh(), 2500);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [
    enabled,
    shellContext?.page_path,
    followUpRefreshDelaysMs,
    refresh,
    refreshWithCatchup,
  ]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const routedTopic = queryTypeRef?.current?.trim() ?? '';
    const timerDelay = routedTopic.length > 0 && tick > 0 ? 0 : debounceMs;
    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const background = hasLoadedRef.current;
      if (background) setRefreshing(true);
      else {
        setLoading(true);
        setError(null);
      }

      void (async () => {
        try {
          const resolvedQueryType =
            queryTypeRef?.current?.trim() || queryType.trim() || 'general_chat';
          const data = await resolveAssistantContext(
            {
              thread_id: threadId ?? undefined,
              query_type: resolvedQueryType,
              user_message: userMessage,
              focus_entity_hint: focusEntityHint ?? undefined,
              shell_context: shellContext ?? undefined,
            },
            controller.signal,
          );
          if (controller.signal.aborted) return;
          setContext(data);
          hasLoadedRef.current = true;
          setError(null);
          if (catchUpActiveRef.current) {
            const after = snapshotLearningPipeline(data);
            const hint = formatLearningPipelineCatchUpHint(catchUpBaselineRef.current, after);
            if (hint) {
              setLearningCatchUpHint(hint);
              const promoted = countNewActiveMemories(catchUpBaselineRef.current, after);
              if (promoted > 0) {
                notifyBrainPreferencesChanged({ scope: 'all' });
              } else {
                notifyBrainDashboardRefresh();
              }
              window.clearTimeout(catchUpHintTimerRef.current);
              catchUpHintTimerRef.current = window.setTimeout(() => {
                setLearningCatchUpHint(null);
              }, 10000);
            }
            if (after) catchUpBaselineRef.current = after;
          }
        } catch (err) {
          if (controller.signal.aborted) return;
          if (!hasLoadedRef.current) {
            setContext(null);
            setError(err instanceof Error ? err.message : 'Context unavailable');
          }
        } finally {
          if (!controller.signal.aborted) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      })();
    }, timerDelay);

    return () => {
      window.clearTimeout(timer);
      abortRef.current?.abort();
    };
  }, [enabled, threadId, userMessage, queryType, debounceMs, focusEntityHint, shellContext, tick]);

  return {
    context,
    loading,
    refreshing,
    error,
    refresh,
    refreshWithCatchup,
    learningCatchUpHint,
    dismissLearningCatchUpHint,
  };
}
